import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Play } from 'lucide-react';
import hmr_waterfront_cover from "../images/projects/HMR Waterfront/hmr_waterfront_cover.png";
import HMR from "../images/projects/HMR Waterfront/HMR.png";
import HP from "../images/projects/HMR Waterfront/HP.png";
import HP2 from "../images/projects/HMR Waterfront/HP2.png";
import saima_tower from "../images/projects/HMR Waterfront/saima-tower.png";
import h_tower from "../images/projects/HMR Waterfront/h-tower.png";
import aa_tower from "../images/projects/HMR Waterfront/aa-tower.png";
import saima_marina from "../images/projects/HMR Waterfront/saima-marina.png";
import beach_terraces from "../images/projects/HMR Waterfront/beach-terraces.png";
import the_tilt from "../images/projects/HMR Waterfront/the-tilt.png";
import goldcrest_bay_sands from "../images/projects/HMR Waterfront/goldcrest-baysands.png";
import HnS from "../images/projects/HMR Waterfront/HnS.png";

/* =========================================================
   YouTube video config
   ========================================================= */
const YOUTUBE_VIDEO_ID = "5Vhgk2Q7cT0";
const YOUTUBE_THUMBNAIL = `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/maxresdefault.jpg`;
const YOUTUBE_THUMBNAIL_FALLBACK = `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/hqdefault.jpg`;
const YOUTUBE_EMBED_URL = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`;

const HMRWaterfrontPage = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [thumbSrc, setThumbSrc] = useState(YOUTUBE_THUMBNAIL);

  // WhatsApp Configuration
  const whatsappNumber = '923487172565';
  const whatsappMessage = encodeURIComponent(
    'Hi EstateGhar! I am interested in HMR Waterfront. Please share further details.'
  );
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  // SEO Configuration
  const seo = {
    title: 'HMR Waterfront Karachi | Luxury Sea-Facing Apartments in DHA Phase 8',
    description: "HMR Waterfront – Karachi's ultimate lifestyle destination. Luxury sea-facing apartments, penthouses & townhouses in DHA Phase 8. 14 high-rise towers, infinity pools, private beach clubhouse & grand mosque. Approved by DHA Karachi. Contact EstateGhar for further details.",
    keywords: 'HMR Waterfront, HMR Waterfront Karachi, luxury apartments Karachi, sea facing apartments DHA, DHA Phase 8 apartments, Karachi waterfront property, HMR Group, penthouses Karachi, beachfront apartments Pakistan, luxury real estate Karachi, EstateGhar',
    canonical: 'https://www.estateghar.com/projects/hmr-waterfront',
    image: hmr_waterfront_cover,
    url: 'https://www.estateghar.com/projects/hmr-waterfront',
    siteName: 'HMR Waterfront',
    locale: 'en_PK',
    type: 'website',
    phone: '+92-348-7172565',
    address: {
      street: 'Abdul Sattar Edhi Avenue, D.H.A. Phase 8 Ext.',
      city: 'Karachi',
      postalCode: '75500',
      country: 'Pakistan',
    },
    geo: {
      latitude: '24.7894',
      longitude: '67.0636',
    },
  };

  // Structured Data (JSON-LD)
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'RealEstateListing',
        name: 'HMR Waterfront',
        description: seo.description,
        url: seo.canonical,
        image: seo.image,
        address: {
          '@type': 'PostalAddress',
          streetAddress: seo.address.street,
          addressLocality: seo.address.city,
          postalCode: seo.address.postalCode,
          addressCountry: seo.address.country,
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: seo.geo.latitude,
          longitude: seo.geo.longitude,
        },
        telephone: seo.phone,
        priceRange: 'PKR',
        numberOfRooms: '1-6 Bedrooms, Penthouses & Townhouses',
      },
      {
        '@type': 'Organization',
        name: 'EstateGhar',
        url: seo.canonical,
        logo: seo.image,
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: seo.phone,
          contactType: 'sales',
          areaServed: 'PK',
          availableLanguage: ['English', 'Urdu'],
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: seo.canonical },
          { '@type': 'ListItem', position: 2, name: 'HMR Waterfront', item: `${seo.canonical}#waterfront` },
          { '@type': 'ListItem', position: 3, name: 'Residences', item: `${seo.canonical}#residences` },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Where is HMR Waterfront located?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'HMR Waterfront is located on Abdul Sattar Edhi Avenue, D.H.A. Phase 8 Ext., Karachi, Pakistan, along the Arabian Sea.',
            },
          },
          {
            '@type': 'Question',
            name: 'What unit types are available at HMR Waterfront?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'HMR Waterfront offers 1, 2, 3, and 4 bedroom apartments, townhouses, and luxury penthouses across its 14 high-rise towers.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is HMR Waterfront approved by DHA?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, HMR Waterfront is an approved project by DHA, Karachi.',
            },
          },
        ],
      },
      {
        '@type': 'VideoObject',
        name: 'HMR Waterfront Karachi — Official Project Introduction',
        description: 'Official introduction video of HMR Waterfront, a luxury sea-facing gated community in DHA Phase 8, Karachi.',
        thumbnailUrl: [YOUTUBE_THUMBNAIL],
        uploadDate: '2024-01-01',
        embedUrl: `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}`,
        contentUrl: `https://www.youtube.com/watch?v=${YOUTUBE_VIDEO_ID}`,
      },
    ],
  };

  return (
    <>
      {/* ===== SEO: React Helmet ===== */}
      <Helmet>
        <title>{seo.title}</title>
        <meta name="title" content={seo.title} />
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="EstateGhar" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="theme-color" content="#b68a4c" />
        <link rel="canonical" href={seo.canonical} />

        <meta name="geo.region" content="PK-SD" />
        <meta name="geo.placename" content="Karachi" />
        <meta name="geo.position" content={`${seo.geo.latitude};${seo.geo.longitude}`} />
        <meta name="ICBM" content={`${seo.geo.latitude}, ${seo.geo.longitude}`} />

        <meta property="og:type" content={seo.type} />
        <meta property="og:url" content={seo.url} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content={seo.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="HMR Waterfront – Luxury Sea-Facing Residences in Karachi" />
        <meta property="og:site_name" content={seo.siteName} />
        <meta property="og:locale" content={seo.locale} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={seo.url} />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={seo.image} />
        <meta name="twitter:image:alt" content="HMR Waterfront – Luxury Sea-Facing Residences in Karachi" />

        <meta name="format-detection" content="telephone=yes" />
        <meta httpEquiv="x-ua-compatible" content="IE=edge" />
        <link rel="alternate" hrefLang="en-PK" href={seo.canonical} />
        <link rel="alternate" hrefLang="ur-PK" href={`${seo.canonical}ur/`} />

        <link rel="preconnect" href="https://images.unsplash.com" />
        <link rel="dns-prefetch" href="https://images.unsplash.com" />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="font-sans bg-white text-gray-800" itemScope itemType="https://schema.org/RealEstateListing">
        <meta itemProp="name" content="HMR Waterfront" />
        <meta itemProp="description" content={seo.description} />
        <meta itemProp="image" content={seo.image} />

        {/* ===== HERO SECTION ===== */}
        <section
          className="relative bg-cover bg-center h-screen flex items-center justify-center"
          style={{
            backgroundImage: hmr_waterfront_cover ? `url(${hmr_waterfront_cover})` : 'none',
          }}
          aria-label="HMR Waterfront Hero"
        >
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative z-10 text-center text-white px-6 max-w-4xl">
            <div className="mb-6">
              <span className="text-5xl" role="img" aria-label="Ocean">🌊</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-wide drop-shadow-lg" itemProp="name">
              HMR WATERFRONT
            </h1>
            <p className="text-xl md:text-2xl mt-4 font-light tracking-wider drop-shadow">
              HEART OF THE OCEAN
            </p>
            <p className="text-lg md:text-xl mt-2 font-light tracking-wider drop-shadow">
              Karachi's Ultimate Lifestyle Destination
            </p>
            <div className="mt-4 inline-block bg-green-700/80 px-4 py-2 rounded-full text-sm">
              ✅ Project Approved By DHA, Karachi
            </div>
            <div className="mt-10 flex justify-center">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold py-4 px-12 rounded-full text-lg shadow-xl transition duration-300 tracking-wide"
                aria-label="Book Now on WhatsApp"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                Book Now
              </a>
            </div>
          </div>
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-20 h-1 bg-[#b68a4c]" />
        </section>

        {/* =========================================================
            VIDEO SECTION — YouTube intro
            ========================================================= */}
        <section className="py-20 px-6 bg-gradient-to-b from-gray-900 to-black" aria-labelledby="intro-video-heading">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#b68a4c]/20 border border-[#b68a4c]/40 text-[#e6c897] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <Play size={16} /> Official Introduction
              </div>
              <h2 id="intro-video-heading" className="text-4xl md:text-5xl font-light text-white mb-4">
                EXPERIENCE <span className="font-bold">HMR WATERFRONT</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg">
                Watch the official introduction to Karachi's ultimate sea-facing lifestyle destination — 14 high-rise towers on the shores of the Arabian Sea.
              </p>
            </div>

            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#b68a4c]/30">
              {showVideo ? (
                <iframe
                  className="w-full h-full"
                  src={YOUTUBE_EMBED_URL}
                  title="HMR Waterfront Karachi — Official Project Introduction"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowVideo(true)}
                  className="group w-full h-full block relative cursor-pointer bg-black"
                  aria-label="Play HMR Waterfront introduction video"
                >
                  <img
                    src={thumbSrc}
                    onError={() => setThumbSrc(YOUTUBE_THUMBNAIL_FALLBACK)}
                    alt="HMR Waterfront Karachi — Official intro video"
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-700 opacity-90 group-hover:opacity-100"
                    loading="lazy"
                  />
                  {/* Dark gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      <span className="absolute inset-0 rounded-full bg-[#b68a4c]/40 animate-ping"></span>
                      <span className="relative flex items-center justify-center w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#b68a4c] hover:bg-[#c99a5c] text-white shadow-2xl transition group-hover:scale-110">
                        <Play size={44} className="ml-1" fill="currentColor" />
                      </span>
                    </div>
                  </div>

                  {/* Caption at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-left">
                    <p className="text-[#e6c897] font-semibold text-sm">Official Intro</p>
                    <p className="text-white text-xl md:text-2xl font-bold">
                      HMR Waterfront — Heart of the Ocean
                    </p>
                  </div>
                </button>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold py-3 px-8 rounded-full transition shadow-lg"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                Book a Site Visit
              </a>
              <a
                href="#residences"
                className="flex items-center justify-center gap-2 bg-[#b68a4c] hover:bg-[#c99a5c] text-white font-semibold py-3 px-8 rounded-full transition shadow-lg"
              >
                Explore Residences
              </a>
            </div>
          </div>
        </section>

        {/* ===== LEADERSHIP SECTION ===== */}
        <section className="py-20 px-6 max-w-7xl mx-auto" aria-labelledby="leadership-heading">
          <div className="text-center mb-16">
            <h2 id="leadership-heading" className="text-4xl md:text-5xl font-light text-gray-800 tracking-wider">
              THE <span className="font-bold">VISIONARIES</span>
            </h2>
            <div className="w-24 h-1 bg-[#b68a4c] mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <article className="bg-gray-50 shadow-xl rounded-2xl overflow-hidden p-6 flex flex-col items-center text-center">
              <img
                src={HMR}
                alt="Dr. Haji M. Rafiq Pardesi – Founder & Chairman of HMR Group"
                className="w-32 h-32 object-cover rounded-full border-4 border-[#b68a4c] mb-4"
                loading="lazy"
              />
              <h3 className="text-xl font-bold text-gray-800">Dr. Haji M. Rafiq Pardesi</h3>
              <p className="text-[#b68a4c] font-semibold text-sm mb-3">Founder & Chairman</p>
              <p className="text-gray-600 text-sm leading-relaxed text-left">
                Began his business journey in 1982 by joining his family business in the UAE. With a vision for diversification and innovation, he expanded into real estate across the UAE, Pakistan, and Africa, delivering successful projects through international partnerships.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mt-2 text-left">
                In 2015, he founded HMR Group and led its growth independently. Alongside his business achievements, he is known for his philanthropic work through Barkati Foundation, supporting free education for underprivileged students. He also represents Pakistani business communities through multiple international business councils and chambers affiliated with FPCCI.
              </p>
            </article>
            <article className="bg-gray-50 shadow-xl rounded-2xl overflow-hidden p-6 flex flex-col items-center text-center">
              <img
                src={HP}
                alt="Mr. Hasnain Pardesi – CEO of HMR Group"
                className="w-32 h-32 object-cover rounded-full border-4 border-[#b68a4c] mb-4"
                loading="lazy"
              />
              <h3 className="text-xl font-bold text-gray-800">Mr. Hasnain Pardesi</h3>
              <p className="text-[#b68a4c] font-semibold text-sm mb-3">CEO</p>
              <p className="text-gray-600 text-sm leading-relaxed text-left">
                A Business Management graduate from the University of Derby, United Kingdom, began his career in Dubai managing the family business's construction and real estate operations. He later moved to Tanzania, Africa, where as Director of Operations and Production, he played a key role in strengthening the Group's presence in the textile sector and African market.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mt-2 text-left">
                He currently oversees the Group's real estate development division as a Board Member of HMR Developers (Pvt.) Limited and HMR Waterfront (Pvt.) Limited, while also serving as Partner & Director of Jahangir Khan Sports Club, leading the development of its modern sports facility.
              </p>
            </article>
            <article className="bg-gray-50 shadow-xl rounded-2xl overflow-hidden p-6 flex flex-col items-center text-center">
              <img
                src={HP2}
                alt="Mr. Hassaan Pardesi – Director of HMR Group"
                className="w-32 h-32 object-cover rounded-full border-4 border-[#b68a4c] mb-4"
                loading="lazy"
              />
              <h3 className="text-xl font-bold text-gray-800">Mr. Hassaan Pardesi</h3>
              <p className="text-[#b68a4c] font-semibold text-sm mb-3">Director</p>
              <p className="text-gray-600 text-sm leading-relaxed text-left">
                A Business Administration graduate from the American University in Dubai, began his career managing a major residential development in Ajman, UAE. Since then, he has played an active role in the growth of HMR Group across real estate and commercial ventures.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mt-2 text-left">
                He has contributed to the success of Goldcrest Mall & Residency, Lahore, led the launch of FuNation, and currently oversees the strategic planning and execution of HMR Waterfront, supporting the Group's vision for sustainable growth and innovation.
              </p>
            </article>
          </div>
        </section>

        {/* ===== ABOUT HMR GROUP ===== */}
        <section className="bg-gray-100 py-20 px-6" aria-labelledby="about-heading">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 id="about-heading" className="text-4xl md:text-5xl font-light text-gray-800 tracking-wide">
                THE WORLD OF <span className="font-bold">HMR</span>
              </h2>
              <div className="w-20 h-1 bg-[#b68a4c] my-6" />
              <h3 className="text-2xl font-semibold text-gray-700 mb-4">
                YOU ARE AT THE HEART OF EVERYTHING WE DO
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Led by Dr. Haji Muhammad Rafiq Pardesi, HMR Group is a real estate development giant in Pakistan and an acclaimed name in the UAE. He began his career in 1982 by joining his Dubai based family business, established by his forefathers over a century ago in British India. His focus on both, trade and regional diversification expanded the business beyond in UAE, Pakistan and Africa. After crafting significant projects in UAE, he laid foundations of a few of the global real estate giants in Pakistan to provide better living to its people and contribute towards the prosperity of the nation. The inauguration of the HMR Group in 2015 provided Dr. Haji Muhammad Rafiq Pardesi with continued growth, allowing him to reach new heights of success and fame.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                With a keen vision to modernize the live-in experience of the Pakistani customer base, HMR's goal is to introduce exclusive real estate development projects across the country, comprising of internationally recognized, high-rise and medium-rise residential and commercial towers.
              </p>
              <h3 className="text-2xl font-semibold text-gray-700 mb-4">
                CRAFTING EXTRAORDINARY LUXURY - OUR PROJECTS
              </h3>
              <p className="text-gray-700 leading-relaxed">
                With a focus on diversification and innovation, HMR Group has steadily become the brand of choice in delivering quality, novelty and service. One of these innovative projects, Goldcrest Views at Jumeirah Lake Tower, won 'The Best Developer Award' for design and quality by Citiscape.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                <span className="text-4xl font-bold text-[#b68a4c]">10+</span>
                <p className="text-gray-600 text-sm">Projects</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                <span className="text-4xl font-bold text-[#b68a4c]">3</span>
                <p className="text-gray-600 text-sm">Countries</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                <span className="text-4xl font-bold text-[#b68a4c]">1982</span>
                <p className="text-gray-600 text-sm">Founded</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                <span className="text-4xl font-bold text-[#b68a4c]">🏆</span>
                <p className="text-gray-600 text-sm">Award Winner</p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== WELCOME TO HMR WATERFRONT ===== */}
        <section className="py-20 px-6 max-w-7xl mx-auto" aria-labelledby="welcome-heading">
          <div className="text-center mb-16">
            <h2 id="welcome-heading" className="text-4xl md:text-5xl font-light text-gray-800">
              WELCOME TO <span className="font-bold">HMR WATERFRONT</span>
            </h2>
            <div className="w-24 h-1 bg-[#b68a4c] mx-auto mt-4" />
            <p className="text-gray-600 max-w-4xl mx-auto mt-6 text-lg leading-relaxed">
              Located at the edge of the Great Arabian Sea, HMR Group is proud to introduce HMR Waterfront. A luxurious gated community that encloses fourteen high-rise residential towers, facilitated with modern amenities and efficient security to enjoy a peaceful, entertaining and contemporary lifestyle. A marvel that encompasses all necessities of family living. HMR Waterfront is surrounded by green open spaces, a mosque, a marine walk promenade and retail outlets for food beverages, and other necessities including exclusive car parking for the residents.
            </p>
            <p className="text-gray-600 max-w-4xl mx-auto mt-4 text-lg leading-relaxed">
              With an enviable premium living experience, HMR Waterfront offers a revolutionary business district. The Commercial Tower entails top-notch business centers, offices, retail spaces, restaurants and coffee shops. Additional facilities include high-speed elevators, abundant basement car parking and podium floors. The project also comprises of a dedicated grid station with backup generators to ensure an uninterrupted supply of electricity and a private filtration plant for sufficient water delivery. A beautiful seafront promenade and a beach clubhouse, overlooking the glistening Arabian Sea, are all set to provide families with the finest social and living experience.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-[#f7f3ee] p-8 rounded-2xl shadow-lg text-center">
              <div className="text-4xl mb-4">🌊</div>
              <h4 className="font-bold text-xl">Seafront Promenade</h4>
              <p className="text-gray-500 text-sm">Overlooking the Arabian Sea</p>
            </div>
            <div className="bg-[#f7f3ee] p-8 rounded-2xl shadow-lg text-center">
              <div className="text-4xl mb-4">🛍️</div>
              <h4 className="font-bold text-xl">Shopping Galleria</h4>
              <p className="text-gray-500 text-sm">Retail & dining experiences</p>
            </div>
            <div className="bg-[#f7f3ee] p-8 rounded-2xl shadow-lg text-center">
              <div className="text-4xl mb-4">🏖️</div>
              <h4 className="font-bold text-xl">Beach Clubhouse</h4>
              <p className="text-gray-500 text-sm">Finest social & living experience</p>
            </div>
            <div className="bg-[#f7f3ee] p-8 rounded-2xl shadow-lg text-center">
              <div className="text-4xl mb-4">🕌</div>
              <h4 className="font-bold text-xl">Grand Mosque</h4>
              <p className="text-gray-500 text-sm">Spiritual serenity</p>
            </div>
          </div>
        </section>

        {/* ===== SEAFRONT PROMENADE & SHOPPING GALLERIA ===== */}
        <section className="bg-gray-100 py-20 px-6" aria-labelledby="promenade-heading">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 id="promenade-heading" className="text-4xl md:text-5xl font-light text-gray-800">
                TOGETHERNESS <span className="font-bold">BY THE SEA</span>
              </h2>
              <div className="w-24 h-1 bg-[#b68a4c] mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <article className="bg-white rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1974&auto=format&fit=crop"
                  alt="Seafront Promenade at HMR Waterfront Karachi"
                  className="w-full h-80 object-cover"
                  loading="lazy"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold">Seafront Promenade</h3>
                  <p className="text-gray-600 mt-2">
                    A beautiful seafront promenade overlooking the glistening Arabian Sea, perfect for evening walks and family gatherings.
                  </p>
                </div>
              </article>
              <article className="bg-white rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?q=80&w=1974&auto=format&fit=crop"
                  alt="Shopping Galleria at HMR Waterfront Karachi"
                  className="w-full h-80 object-cover"
                  loading="lazy"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold">Shopping Galleria</h3>
                  <p className="text-gray-600 mt-2">
                    Retail outlets for food, beverages, and other necessities, including exclusive car parking for residents.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* ===== BEACH CLUBHOUSE & GRAND MOSQUE ===== */}
        <section className="py-20 px-6 max-w-7xl mx-auto" aria-labelledby="clubhouse-heading">
          <h2 id="clubhouse-heading" className="sr-only">Beach Clubhouse and Grand Mosque</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <article className="bg-white rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?q=80&w=1974&auto=format&fit=crop"
                alt="Beach Clubhouse at HMR Waterfront Karachi"
                className="w-full h-80 object-cover"
                loading="lazy"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold">Beach Clubhouse</h3>
                <p className="text-gray-600 mt-2">
                  A stunning beach clubhouse overlooking the Arabian Sea, providing families with the finest social and living experience.
                </p>
              </div>
            </article>
            <article className="bg-white rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS9j5iIV6TN9rh_aI0i1Q_JMkChOCEU0E1CSRle-Kku6ChgYHiXgmO_0acR&s=10"
                alt="Grand Mosque at HMR Waterfront Karachi"
                className="w-full h-80 object-cover"
                loading="lazy"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold">Grand Mosque</h3>
                <p className="text-gray-600 mt-2">
                  A magnificent mosque with a soaring minaret and a stunning blue dome, offering spiritual serenity to residents.
                </p>
              </div>
            </article>
          </div>
        </section>

        {/* ===== PROJECTS OVERVIEW MAP ===== */}
        <section className="bg-gray-100 py-20 px-6" aria-labelledby="projects-overview-heading">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 id="projects-overview-heading" className="text-4xl md:text-5xl font-light text-gray-800">
                OUR <span className="font-bold">PROJECTS</span>
              </h2>
              <div className="w-24 h-1 bg-[#b68a4c] mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              {[
                '1. HI Tower',
                '2. Saima Tower',
                '3. AA Waterfront',
                '4. Saima Marina Residence',
                '5. H&S Residence',
                '6. GoldCrest Bay Sands',
                '7. Beach Terraces',
                '8. GoldCrest Bay Sands 2',
                '9. The TILT',
                '10. AA Beachfront'
              ].map((project, i) => (
                <div key={i} className="bg-white p-4 rounded-xl shadow-md text-sm font-semibold text-gray-700">
                  {project}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== H1 Tower – FLAGSHIP ===== */}
        <section className="py-20 px-6 max-w-7xl mx-auto" aria-labelledby="luxe-heading">
          <div className="text-center mb-16">
            <h2 id="luxe-heading" className="text-4xl md:text-5xl font-light text-gray-800">
              LUXE <span className="font-bold">LIVING</span>
            </h2>
            <div className="w-24 h-1 bg-[#b68a4c] mx-auto mt-4" />
          </div>
          <article className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-50 rounded-2xl shadow-xl p-4 flex items-center justify-center h-[600px]">
              <img
                src={h_tower}
                alt="H1 Tower – Flagship Tower at HMR Waterfront"
                className="max-w-full max-h-full object-contain"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-800 mb-4">H1 Tower – Flagship</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                H1 Tower is the Flagship Tower of HMR Waterfront. A stunning skyscraper that will inspire the residents and visitors from around the world. It is a profound architecture, comprising of Ground + 34 floors of luxury living with a double heighted grand lobby to welcome you.
              </p>
              <div className="mb-4">
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Unit Types:</h4>
                <p className="text-gray-600">Townhouse • 1 Bedroom • 2 Bedroom • 3 Bedroom • 4 Bedroom • Penthouse</p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Features & Amenities:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 list-disc list-inside">
                  <li>Elegant Reception</li>
                  <li>24 hours Concierge</li>
                  <li>Sea and Partial-Sea Facing Apartments</li>
                  <li>Infinity Pool</li>
                  <li>Multi-Purpose Hall</li>
                  <li>Fully Equipped Gym</li>
                  <li>Spacious Bedrooms</li>
                  <li>Modern Bathrooms</li>
                  <li>Built in Kitchen</li>
                  <li>Maid's Room</li>
                  <li>High Speed Elevators</li>
                  <li>Retail Outlets</li>
                  <li>Fire life safety certified lifts</li>
                  <li>Evacuation System</li>
                  <li>Multiple Floors of Covered Parking</li>
                  <li>24/7 CCTV Surveillance</li>
                  <li>24/7 Power Backup</li>
                </ul>
              </div>
            </div>
          </article>
        </section>

        {/* ===== SAIMA TOWER ===== */}
        <section className="bg-gray-100 py-20 px-6" aria-labelledby="saima-heading">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h3 id="saima-heading" className="text-3xl font-bold text-gray-800 mb-4">Saima Tower</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Saima Tower is a Ground + 40 storey residential project of iconic significance coming up right in front of sea at HMR Waterfront. The project comprises of ultra-luxury apartments provided with almost every conceivable amenity of the modern era.
              </p>
              <div className="mb-4">
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Unit Types:</h4>
                <p className="text-gray-600">Townhouse • 1 Bedroom • 2 Bedroom • 3 Bedroom • 4 Bedroom & Penthouse with Infinity Pool</p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Features & Amenities:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 list-disc list-inside">
                  <li>24 ft Height Lobby with Concierge</li>
                  <li>Lounge Area</li>
                  <li>Multiple Floors of Covered Parking</li>
                  <li>Swimming Pool</li>
                  <li>State of the art Gym</li>
                  <li>Prayer Area</li>
                  <li>Spacious Bedroom</li>
                  <li>Children Play area</li>
                  <li>7 floors of Car Parking</li>
                  <li>24 ft Height Retail Outlets</li>
                  <li>24/7 CCTV Surveillance</li>
                  <li>Backup Generators</li>
                  <li>Variable Refrigerant Flow Units</li>
                </ul>
              </div>
            </div>
            <div className="order-1 lg:order-2 bg-gray-50 rounded-2xl shadow-xl p-4 flex items-center justify-center h-[600px]">
              <img
                src={saima_tower}
                alt="Saima Tower at HMR Waterfront Karachi"
                className="max-w-full max-h-full object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* ===== AA WATERFRONT ===== */}
        <section className="py-20 px-6 max-w-7xl mx-auto" aria-labelledby="aa-heading">
          <article className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-50 rounded-2xl shadow-xl p-4 flex items-center justify-center h-[600px]">
              <img
                src={aa_tower}
                alt="AA Waterfront at HMR Waterfront Karachi"
                className="max-w-full max-h-full object-contain"
                loading="lazy"
              />
            </div>
            <div>
              <h3 id="aa-heading" className="text-3xl font-bold text-gray-800 mb-4">AA Waterfront</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                AA Waterfront is a masterpiece that showcases our passion for extraordinary living at HMR Waterfront. The Ground + 37 storey project comprises of stunning apartments and expensive views of the sea, the smart luxurious apartments and smart duplex penthouses are rewriting comfortable lifestyles.
              </p>
              <div className="mb-4">
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Unit Types:</h4>
                <p className="text-gray-600">1 Bedroom • 2 Bedroom • 3 Bedroom • 4 Bedroom & 6 Bedroom Penthouse with Pool</p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Features & Amenities:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 list-disc list-inside">
                  <li>Double Heighted Grand Lobby</li>
                  <li>Smart Apartments</li>
                  <li>Lounge Area</li>
                  <li>Community Hall</li>
                  <li>Indoor & Outdoor Swimming Pool</li>
                  <li>Double Heighted Showrooms</li>
                  <li>Meeting Room</li>
                  <li>Prayer Area</li>
                  <li>Sauna</li>
                  <li>Steam Room</li>
                  <li>Children's Play Area</li>
                  <li>Gaming Room</li>
                  <li>Gym</li>
                  <li>Multiple Floors of Covered Parking</li>
                  <li>Smart Apartments</li>
                </ul>
              </div>
            </div>
          </article>
        </section>

        {/* ===== Saima Marina Residence ===== */}
        <section className="bg-gray-100 py-20 px-6" aria-labelledby="saima-marina-heading">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h3 id="saima-marina-heading" className="text-3xl font-bold text-gray-800 mb-4">Saima Marina Residence</h3>
              <div className="mb-4">
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Unit Types:</h4>
                <p className="text-gray-600">1 Bedroom • 2 Bedroom • 3 Bedroom • 4 Bedroom • Penthouse</p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Features & Amenities:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 list-disc list-inside">
                  <li>Double Height Shops</li>
                  <li>Double Height Lobby</li>
                  <li>Yoga Area (Open Air)</li>
                  <li>Showers</li>
                  <li>Salon</li>
                  <li>Gym</li>
                  <li>Prayer Area</li>
                  <li>Kids Play Area</li>
                  <li>Changing Rooms</li>
                  <li>7 Floors for Covered Parking</li>
                  <li>5 Passenger Lifts (1 FLS & 1 Cargo)</li>
                  <li>Indoor Games</li>
                  <li>Open & Covered Dining Area</li>
                  <li>Sauna & Jacuzzi</li>
                </ul>
              </div>
            </div>
            <div className="order-1 lg:order-2 bg-gray-50 rounded-2xl shadow-xl p-4 flex items-center justify-center h-[600px]">
              <img
                src={saima_marina}
                alt="Saima Marina Residence at HMR Waterfront Karachi"
                className="max-w-full max-h-full object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* ===== H&S RESIDENCE ===== */}
        <section className="py-20 px-6 max-w-7xl mx-auto" aria-labelledby="hs-heading">
          <article className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-50 rounded-2xl shadow-xl p-4 flex items-center justify-center h-[600px]">
              <img
                src={HnS}
                alt="H&S Residence at HMR Waterfront Karachi"
                className="max-w-full max-h-full object-contain"
                loading="lazy"
              />
            </div>
            <div>
              <h3 id="hs-heading" className="text-3xl font-bold text-gray-800 mb-4">H&S Residence</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Welcome to H&S Residence, a coastal haven standing 39 storeys tall along at HMR Waterfront's vibrant skyline. Inspired by the Japanese Nami design philosophy, our architecture reflects the fluidity of ocean waves.
              </p>
              <div className="mb-4">
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Unit Types:</h4>
                <p className="text-gray-600">1 Bedroom • 2 Bedroom • 3 Bedroom & 4 Bedroom with Plunge Pools • Penthouse</p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Features & Amenities:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 list-disc list-inside">
                  <li>Architectural Designed By Nikken Sekkei</li>
                  <li>3 & 4 Bedroom Apartments With Plunge Pools</li>
                  <li>Infinity Pool</li>
                  <li>Creative Workspaces</li>
                  <li>Multiple Floors of Covered Parking</li>
                  <li>Gym</li>
                  <li>High Speed Elevators</li>
                  <li>Snooker Lounge</li>
                  <li>Double Height Shops</li>
                  <li>Double Height Lobby</li>
                  <li>Kids Play Area</li>
                  <li>Social Area</li>
                  <li>8 Lifts (1 Fls & 1 Cargo)</li>
                  <li>Spa & Sauna</li>
                </ul>
              </div>
            </div>
          </article>
        </section>

        {/* ===== Beach Terraces ===== */}
        <section className="bg-gray-100 py-20 px-6" aria-labelledby="beach-terraces-heading">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h3 id="beach-terraces-heading" className="text-3xl font-bold text-gray-800 mb-4">Beach Terraces</h3>
              <div className="mb-4">
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Unit Types:</h4>
                <p className="text-gray-600">1 Bedroom • 2 Bedroom • 3 Bedroom • Townhouse • Penthouse</p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-700 mb-2">Features & Amenities:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 list-disc list-inside">
                  <li>Infinity Pool</li>
                  <li>Alluring Floor</li>
                  <li>Community Hall</li>
                  <li>Cafeteria</li>
                  <li>Gym</li>
                  <li>Beach & Sea Facing Apartments</li>
                  <li>Business Centre</li>
                  <li>Multiple Floors of Covered Parking</li>
                  <li>Sauna</li>
                  <li>Day Care Center</li>
                  <li>Prayer Hall</li>
                  <li>Indoor Games</li>
                  <li>Kids Play Area</li>
                  <li>Double Heighted Retail Outlets</li>
                </ul>
              </div>
            </div>
            <div className="order-1 lg:order-2 bg-gray-50 rounded-2xl shadow-xl p-4 flex items-center justify-center h-[600px]">
              <img
                src={beach_terraces}
                alt="Beach Terraces at HMR Waterfront Karachi"
                className="max-w-full max-h-full object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* ===== UPCOMING PROJECTS ===== */}
        <section className="py-20 px-6 max-w-7xl mx-auto" aria-labelledby="upcoming-heading">
          <div className="text-center mb-16">
            <h2 id="upcoming-heading" className="text-4xl md:text-5xl font-light text-gray-800">
              UPCOMING <span className="font-bold">PROJECTS</span>
            </h2>
            <div className="w-24 h-1 bg-[#b68a4c] mx-auto mt-4" />
          </div>

          {/* Project 1: Beach Terraces */}
          <article className="bg-white p-8 rounded-2xl shadow-xl mb-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-center h-[500px]">
                <img
                  src={beach_terraces}
                  alt="Beach Terraces – Upcoming Project at HMR Waterfront"
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Beach Terraces</h3>
                <p className="text-[#b68a4c] font-semibold mb-3">1, 2 & 3 Bedroom Apartments • Townhouse • Penthouse</p>
                <p className="text-gray-600 text-sm mb-3">Beach & Sea Facing Apartments with Infinity Pool</p>
                <h4 className="font-semibold text-gray-700 mb-2">Features & Amenities:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-gray-600 list-disc list-inside">
                  <li>Infinity Pool</li>
                  <li>Alluring Floor</li>
                  <li>Community Hall</li>
                  <li>Cafeteria</li>
                  <li>Gym</li>
                  <li>Beach & Sea Facing Apartments</li>
                  <li>Business Centre</li>
                  <li>Multiple Floors of Covered Parking</li>
                  <li>Sauna</li>
                  <li>Day Care Center</li>
                  <li>Prayer Hall</li>
                  <li>Indoor Games</li>
                  <li>Kids Play Area</li>
                  <li>Double Heighted Retail Outlets</li>
                </ul>
              </div>
            </div>
          </article>

          {/* Project 2: GoldCrest Bay Sands */}
          <article className="bg-white p-8 rounded-2xl shadow-xl mb-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">GoldCrest Bay Sands</h3>
                <p className="text-[#b68a4c] font-semibold mb-3">Luxury Sea-Facing Residences</p>
                <p className="text-gray-600 text-sm mb-3">
                  A premium addition to HMR Waterfront's skyline, GoldCrest Bay Sands offers luxury sea-facing residences with world-class amenities and breathtaking views of the Arabian Sea.
                </p>
                <h4 className="font-semibold text-gray-700 mb-2">Highlights:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-gray-600 list-disc list-inside">
                  <li>Sea-Facing Apartments</li>
                  <li>Infinity Pool</li>
                  <li>Fully Equipped Gymnasium</li>
                  <li>24/7 Security</li>
                  <li>Covered Parking</li>
                  <li>High-Speed Elevators</li>
                  <li>Retail Outlets</li>
                  <li>Prayer Area</li>
                </ul>
              </div>
              <div className="order-1 lg:order-2 bg-gray-50 rounded-xl p-4 flex items-center justify-center h-[500px]">
                <img
                  src={goldcrest_bay_sands}
                  alt="GoldCrest Bay Sands – Upcoming Project at HMR Waterfront"
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          </article>

          {/* Project 3: The TILT */}
          <article className="bg-white p-8 rounded-2xl shadow-xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-center h-[500px]">
                <img
                  src={the_tilt}
                  alt="The TILT – Upcoming Project at HMR Waterfront"
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">The TILT</h3>
                <p className="text-[#b68a4c] font-semibold mb-3">1, 2, 3 & 4 Bedroom Apartments • Simplex, Triplex Penthouse</p>
                <p className="text-gray-600 text-sm mb-3">
                  Santorini-Inspired Sunken Slab Pools • Separate Access for Maid Room • Signature Tilted Architecture
                </p>
                <h4 className="font-semibold text-gray-700 mb-2">Features & Amenities:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-gray-600 list-disc list-inside">
                  <li>Infinity Swimming Pool</li>
                  <li>Fully Equipped Gymnasium</li>
                  <li>Sunken Slab Pools</li>
                  <li>Spa & Sauna</li>
                  <li>Exclusive Cigar Lounge</li>
                  <li>Dedicated Prayer Area</li>
                  <li>Grand Lobby</li>
                  <li>Double-height boutique outlets</li>
                  <li>Community Hall</li>
                  <li>Day Care Facility</li>
                  <li>EV Charging Port</li>
                </ul>
                <h4 className="font-semibold text-gray-700 mt-3 mb-2">Convenience & Infrastructure:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-gray-600 list-disc list-inside">
                  <li>High-Speed Elevators</li>
                  <li>Fire & Life Safety (FLS) Lifts</li>
                  <li>8 Levels of Dedicated Parking</li>
                  <li>24/7 Security with CCTV Surveillance</li>
                </ul>
              </div>
            </div>
          </article>
        </section>

        {/* ===== LOCATION MAP ===== */}
        <section className="bg-gray-100 py-20 px-6" aria-labelledby="location-heading">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 id="location-heading" className="text-4xl md:text-5xl font-light text-gray-800">
                WHISPERING <span className="font-bold">LOCATION</span>
              </h2>
              <div className="w-24 h-1 bg-[#b68a4c] mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-white p-6 rounded-2xl shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=1974&auto=format&fit=crop"
                  alt="HMR Waterfront Location Map – DHA Phase 8, Karachi"
                  className="w-full h-96 object-cover rounded-xl"
                  loading="lazy"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Distances from HMR Waterfront</h3>
                <div className="space-y-3">
                  {[
                    { name: 'Clock Tower', distance: '4.0 km' },
                    { name: 'Dolmen Mall Clifton', distance: '7.9 km' },
                    { name: 'DHA Golf Club', distance: '5.2 km' },
                    { name: 'Ramada Hotel', distance: '7.3 km' },
                    { name: 'Bay View Academy', distance: '5.0 km' },
                    { name: 'Do Darya Restaurant', distance: '3.0 km' },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between bg-white p-4 rounded-xl shadow-sm">
                      <span className="text-gray-700 font-medium">{item.name}</span>
                      <span className="text-[#b68a4c] font-bold">{item.distance}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FOOTER / ESTATEGHAR CONTACT ===== */}
        <footer className="bg-gray-900 text-white py-16 px-6" itemScope itemType="https://schema.org/RealEstateAgent">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-3xl font-bold mb-4" itemProp="name">EstateGhar</h3>
              <p className="text-gray-400 max-w-md mb-6">
                For further details about HMR Waterfront, get in touch with EstateGhar — your trusted real estate partner.
              </p>
              <div className="space-y-3">
                <p className="text-gray-300">
                  💬 WhatsApp:{' '}
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#25D366] font-semibold hover:text-[#1ebe57] transition"
                    itemProp="telephone"
                  >
                    +92 348 7172565
                  </a>
                </p>
                <p className="text-gray-300">
                  📞 Phone:{' '}
                  <a href="tel:+923487172565" className="text-white font-semibold hover:text-[#b68a4c] transition">
                    +92 348 7172565
                  </a>
                </p>
              </div>

              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold py-3 px-8 rounded-full shadow-lg transition duration-300"
                aria-label="Chat with EstateGhar on WhatsApp"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                Chat on WhatsApp
              </a>
            </div>

            <div className="flex flex-col space-y-3">
              <h4 className="text-xl font-semibold mb-2">Quick Links</h4>
              <a href="#residences" className="text-gray-400 hover:text-[#b68a4c] transition">Residences</a>
              <a href="#amenities" className="text-gray-400 hover:text-[#b68a4c] transition">Amenities</a>
              <a href="#upcoming-heading" className="text-gray-400 hover:text-[#b68a4c] transition">Upcoming Projects</a>
              <a href="#location-heading" className="text-gray-400 hover:text-[#b68a4c] transition">Location</a>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:text-[#1ebe57] transition font-semibold">
                Contact Sales
              </a>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500 text-sm">
            © 2026 EstateGhar. All rights reserved.
          </div>
        </footer>
      </main>
    </>
  );
};

export default HMRWaterfrontPage;